import React, { useEffect, useMemo, useState } from 'react';
import { auth } from '../../../../auth/clientAppAuth';
import { useStorefrontAuth } from '../../../auth/context/StorefrontAuthContext';
import { getStorefrontCompanyByIdOrNull } from '../../../../api/storefront/client';
import { upsertPublicClient } from '../../../../api/companyRecords';
import { GlobalUserService } from '../../../storefront-shared/services/GlobalUserService';
import StorefrontProfileAvatar from '../components/StorefrontProfileAvatar';
import StorefrontProfileCustomFields from '../components/StorefrontProfileCustomFields';
import StorefrontDocuments from '../components/StorefrontDocuments';
import { formatPhoneInput } from '../utils/phoneFormat';
import { FormSkeleton } from '../../../../components/loading/skeletons';
import '../styles/StorefrontProfile.css';

const buildEditedState = (clientUser, customFields) => {
  const base = {
    firstName: clientUser?.firstName || '',
    lastName: clientUser?.lastName || '',
    email: clientUser?.email || '',
    phone: clientUser?.phone || '',
    address: {
      street1: clientUser?.address?.street1 || '',
      street2: clientUser?.address?.street2 || '',
      city: clientUser?.address?.city || '',
      state: clientUser?.address?.state || '',
      zipCode: clientUser?.address?.zipCode || '',
    },
    emergencyContact: {
      name: clientUser?.emergencyContact?.name || '',
      phone: clientUser?.emergencyContact?.phone || '',
      relationship: clientUser?.emergencyContact?.relationship || '',
    },
  };

  customFields.forEach((field) => {
    if (field.type === 'checkbox') {
      base[field.id] = Boolean(clientUser?.[field.id]);
    } else {
      base[field.id] = clientUser?.[field.id] ?? '';
    }
  });

  return base;
};

const StorefrontProfilePage = () => {
  const { storefrontClient, loading: authLoading, refreshStorefrontAuth, patchStorefrontClient, companyData } = useStorefrontAuth();

  const [customFields, setCustomFields] = useState([]);
  const [mobileBusinessEnabled, setMobileBusinessEnabled] = useState(false);
  const [editedData, setEditedData] = useState(() => buildEditedState(null, []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const companyId = storefrontClient?.companyId || companyData?.id;

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(''), 3200);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    const loadCompany = async () => {
      if (!companyId) return;
      try {
        const data = await getStorefrontCompanyByIdOrNull(companyId);
        if (data) {
          setCustomFields(data?.settings?.customFields || []);
          setMobileBusinessEnabled(Boolean(data?.businessInfo?.mobileBusiness));
        }
      } catch (e) {
        console.error('Storefront profile: company load', e);
      }
    };
    loadCompany();
  }, [companyId]);

  useEffect(() => {
    if (storefrontClient) {
      setEditedData(buildEditedState(storefrontClient, customFields));
    }
  }, [storefrontClient, customFields]);

  const displayName = useMemo(() => {
    const fn = editedData.firstName || '';
    const ln = editedData.lastName || '';
    return `${fn} ${ln}`.trim() || storefrontClient?.name || 'Your profile';
  }, [editedData.firstName, editedData.lastName, storefrontClient?.name]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!storefrontClient?.id || !storefrontClient?.companyId) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (!editedData.firstName?.trim() || !editedData.lastName?.trim()) {
        throw new Error('First name and last name are required.');
      }

      const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
      if (editedData.phone && !phoneRegex.test(editedData.phone)) {
        throw new Error('Please enter a valid phone number.');
      }
      if (editedData.emergencyContact?.phone && !phoneRegex.test(editedData.emergencyContact.phone)) {
        throw new Error('Please enter a valid emergency contact phone.');
      }

      customFields.forEach((field) => {
        if (field.required) {
          const v = editedData[field.id];
          let ok = true;
          if (field.type === 'checkbox') {
            ok = v === true;
          } else if (field.type === 'number') {
            ok = v !== '' && v != null && !Number.isNaN(Number(v));
          } else {
            ok = String(v ?? '').trim().length > 0;
          }
          if (!ok) {
            throw new Error(`Please fill in: ${field.label || field.id}`);
          }
        }
      });

      const updateData = {
        firstName: editedData.firstName.trim(),
        lastName: editedData.lastName.trim(),
        phone: editedData.phone,
        name: `${editedData.firstName.trim()} ${editedData.lastName.trim()}`.trim(),
        searchName: `${editedData.firstName.trim()} ${editedData.lastName.trim()}`.toLowerCase(),
        emergencyContact: {
          name: editedData.emergencyContact?.name || '',
          phone: editedData.emergencyContact?.phone || '',
          relationship: editedData.emergencyContact?.relationship || '',
        },
        updatedAt: new Date(),
      };

      customFields.forEach((field) => {
        if (field.type === 'checkbox') {
          updateData[field.id] = Boolean(editedData[field.id]);
        } else {
          updateData[field.id] = editedData[field.id] ?? '';
        }
      });

      if (mobileBusinessEnabled) {
        updateData.address = {
          street1: editedData.address?.street1 || '',
          street2: editedData.address?.street2 || '',
          city: editedData.address?.city || '',
          state: editedData.address?.state || '',
          zipCode: editedData.address?.zipCode || '',
        };
      }

      await upsertPublicClient({
        companyId: storefrontClient.companyId,
        id: storefrontClient.id,
        record: {
          ...updateData,
          updatedAt: new Date().toISOString(),
        },
      });

      const profilePatch = {
        firstName: updateData.firstName,
        lastName: updateData.lastName,
        phone: updateData.phone,
        emergencyContact: updateData.emergencyContact,
      };
      if (mobileBusinessEnabled) {
        profilePatch.address = updateData.address;
      }
      await GlobalUserService.updateCompanyProfile(storefrontClient.companyId, profilePatch);

      await refreshStorefrontAuth(auth.currentUser, { silent: true });
      setSuccess('Profile saved.');
    } catch (err) {
      setError(err.message || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (storefrontClient) {
      setEditedData(buildEditedState(storefrontClient, customFields));
    }
    setError('');
    setSuccess('');
  };

  if (authLoading) {
    return (
      <div className="sf-profile">
        <FormSkeleton fields={6} label="Loading profile" />
      </div>
    );
  }

  if (!storefrontClient) {
    return null;
  }

  return (
    <div className="sf-profile">
      <header className="sf-profile__header">
        <h1 className="sf-profile__title">Profile</h1>
        <p className="sf-profile__lead">
          Update how you appear to {companyData?.name || 'this business'}. Click your photo to change it.
        </p>
      </header>

      <div className="sf-profile__hero">
        <StorefrontProfileAvatar
          clientUser={storefrontClient}
          onAvatarSaved={(avatarUrl) => patchStorefrontClient({ avatarUrl })}
          onRefresh={() => refreshStorefrontAuth(auth.currentUser, { silent: true })}
        />
        <div className="sf-profile__hero-text">
          <p className="sf-profile__display-name">{displayName}</p>
        </div>
      </div>

      <form className="sf-profile__form" onSubmit={handleSave}>
        <section className="sf-profile__section">
          <h2 className="sf-profile__section-title">Contact</h2>
          <div className="sf-profile__grid">
            <label className="sf-profile__field">
              <span className="sf-profile__label">First name</span>
              <input className="sf-profile__input" value={editedData.firstName} onChange={(e) => setEditedData({ ...editedData, firstName: e.target.value })} required />
            </label>
            <label className="sf-profile__field">
              <span className="sf-profile__label">Last name</span>
              <input className="sf-profile__input" value={editedData.lastName} onChange={(e) => setEditedData({ ...editedData, lastName: e.target.value })} required />
            </label>
            <label className="sf-profile__field sf-profile__field--full">
              <span className="sf-profile__label">Email</span>
              <input className="sf-profile__input sf-profile__input--readonly" readOnly value={editedData.email} />
            </label>
            <label className="sf-profile__field sf-profile__field--full">
              <span className="sf-profile__label">Phone</span>
              <input
                className="sf-profile__input"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="(555) 555-5555"
                maxLength={14}
                value={editedData.phone}
                onChange={(e) => setEditedData({ ...editedData, phone: formatPhoneInput(e.target.value) })}
              />
            </label>
          </div>
        </section>

        <section className="sf-profile__section">
          <h2 className="sf-profile__section-title">Emergency contact</h2>
          <div className="sf-profile__grid">
            <label className="sf-profile__field sf-profile__field--full">
              <span className="sf-profile__label">Name</span>
              <input
                className="sf-profile__input"
                value={editedData.emergencyContact?.name || ''}
                onChange={(e) => setEditedData({
                  ...editedData,
                  emergencyContact: { ...editedData.emergencyContact, name: e.target.value },
                })}
              />
            </label>
            <label className="sf-profile__field">
              <span className="sf-profile__label">Relationship</span>
              <input
                className="sf-profile__input"
                value={editedData.emergencyContact?.relationship || ''}
                onChange={(e) => setEditedData({
                  ...editedData,
                  emergencyContact: { ...editedData.emergencyContact, relationship: e.target.value },
                })}
                placeholder="e.g. Spouse"
              />
            </label>
            <label className="sf-profile__field">
              <span className="sf-profile__label">Phone</span>
              <input
                className="sf-profile__input"
                type="tel"
                inputMode="numeric"
                maxLength={14}
                placeholder="(555) 555-5555"
                value={editedData.emergencyContact?.phone || ''}
                onChange={(e) => setEditedData({
                  ...editedData,
                  emergencyContact: {
                    ...editedData.emergencyContact,
                    phone: formatPhoneInput(e.target.value),
                  },
                })}
              />
            </label>
          </div>
        </section>

        <StorefrontProfileCustomFields fields={customFields} editedData={editedData} setEditedData={setEditedData} />

        {mobileBusinessEnabled ? (
          <section className="sf-profile__section">
            <h2 className="sf-profile__section-title">Service address</h2>
            <div className="sf-profile__grid">
              <label className="sf-profile__field sf-profile__field--full">
                <span className="sf-profile__label">Street address</span>
                <input
                  className="sf-profile__input"
                  value={editedData.address?.street1 || ''}
                  onChange={(e) => setEditedData({
                    ...editedData,
                    address: { ...editedData.address, street1: e.target.value },
                  })}
                />
              </label>
              <label className="sf-profile__field sf-profile__field--full">
                <span className="sf-profile__label">Apt, suite (optional)</span>
                <input
                  className="sf-profile__input"
                  value={editedData.address?.street2 || ''}
                  onChange={(e) => setEditedData({
                    ...editedData,
                    address: { ...editedData.address, street2: e.target.value },
                  })}
                />
              </label>
              <label className="sf-profile__field">
                <span className="sf-profile__label">City</span>
                <input
                  className="sf-profile__input"
                  value={editedData.address?.city || ''}
                  onChange={(e) => setEditedData({
                    ...editedData,
                    address: { ...editedData.address, city: e.target.value },
                  })}
                />
              </label>
              <label className="sf-profile__field">
                <span className="sf-profile__label">State</span>
                <input
                  className="sf-profile__input"
                  value={editedData.address?.state || ''}
                  onChange={(e) => setEditedData({
                    ...editedData,
                    address: { ...editedData.address, state: e.target.value },
                  })}
                />
              </label>
              <label className="sf-profile__field">
                <span className="sf-profile__label">ZIP</span>
                <input
                  className="sf-profile__input"
                  value={editedData.address?.zipCode || ''}
                  onChange={(e) => setEditedData({
                    ...editedData,
                    address: { ...editedData.address, zipCode: e.target.value },
                  })}
                />
              </label>
            </div>
          </section>
        ) : null}

        <section className="sf-profile__section sf-profile__section--muted">
          <h2 className="sf-profile__section-title">Password</h2>
          <p className="sf-profile__hint">
            Use the same reset flow as sign-in if you need a new password.
          </p>
          <a className="sf-profile__link-out" href="/app/forgot-password" target="_blank" rel="noopener noreferrer">
            Open password reset
          </a>
        </section>

        {error ? (
          <p className="sf-profile__banner sf-profile__banner--error" role="alert">{error}</p>
        ) : null}
        {success ? (
          <p className="sf-profile__banner sf-profile__banner--success" role="status">{success}</p>
        ) : null}

        <div className="sf-profile__actions">
          <button type="button" className="sf-profile-btn sf-profile-btn--ghost" onClick={handleReset} disabled={saving}>
            Reset
          </button>
          <button type="submit" className="sf-profile-btn sf-profile-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>

      <StorefrontDocuments
        companyId={storefrontClient.companyId}
        clientId={storefrontClient.id}
      />
    </div>
  );
};

export default StorefrontProfilePage;
